import type { ApiClient } from "@/clients/ApiClient";
import { fromEither } from "./compat";
import { partNumber } from "@/core/seriesPart";

interface EpisodesPage {
    episodes: Episode[];
    /** All the podcast's published episodes, not just this page */
    count: number;
}

export interface EpisodeWithNeighbours {
    episode: Episode;
    next: Episode | null;
    previous: Episode | null;
}

export interface PodcastsApi {
    /** The podcasts in a language that are real podcasts (not feeds without episodes), as a short list */
    byLanguage(lang: string): Promise<Podcast[]>;
    /** The newest podcasts, newest first */
    latest(limit: number, lang: string): Promise<Podcast[]>;
    /** The podcast with its chart history and collections. Throws when there is no such podcast. */
    bySlug(slug: string): Promise<Podcast>;
    /** One page (30) of a podcast's published episodes, newest first */
    episodes(podcastId: number, page: number): Promise<EpisodesPage>;
    /** Throws when the podcast has no such episode */
    episodeBySlug(podcastId: number, slug: string): Promise<EpisodeWithNeighbours>;
    /** The numbers of the seasons the podcast has, for its season tabs */
    seasons(podcastId: number): Promise<number[]>;
    season(podcastId: number, season: number): Promise<Season>;
    stats(): Promise<SiteStats>;
    /** The podcasts with these ids, by name */
    byIds(ids: number[]): Promise<Podcast[]>;
    /** The podcasts most like this one, best first. An empty list when there are none. */
    similar(podcastId: number): Promise<Podcast[]>;
    /**
     * Episodes from other podcasts about the same thing as this one, best first. An empty list when there are
     * none, and only episodes that carry their podcast: a row needs it to name the show and to link to it.
     */
    similarEpisodes(episodeId: number): Promise<Episode[]>;
    /**
     * The multi-part series that the podcast's episodes belong to, the one with most parts first, each with its parts
     * in part order. An empty list when there are none.
     */
    series(podcastId: number): Promise<Series[]>;
}

/** PHP wraps each similar episode as { episode, similarity_score, similarity_type }, Go sends the episode */
const episodeOf = (item: Episode | { episode: Episode }): Episode =>
    "episode" in item && typeof item.episode === "object" ? item.episode : (item as Episode);

export const createPodcastsApi = (client: ApiClient): PodcastsApi => ({
    async byLanguage(lang) {
        return fromEither<Podcast[]>(
            await client.get(`/podcasts?lang=${encodeURIComponent(lang)}&pod=1&limit=100`),
            "podcasts"
        );
    },

    async latest(limit, lang) {
        return fromEither<Podcast[]>(
            await client.get(
                `/podcasts?sort=created_at&dir=desc&limit=${limit}&pod=1&lang=${encodeURIComponent(lang)}`
            ),
            "podcasts"
        );
    },

    async bySlug(slug) {
        const podcast = fromEither<Podcast | null>(
            await client.get(`/podcasts/bySlug/${encodeURIComponent(slug)}`),
            "podcast"
        );
        if (!podcast) {
            throw new Error(`Podcast not found: ${slug}`);
        }
        // The Go API sends empty lists as [] too, but a PHP podcast without a chart history may omit them
        return { ...podcast, entries: podcast.entries ?? [], collections: podcast.collections ?? [] };
    },

    // Both APIs answer with { episodes, count, ... }: PHP as the body, Go as the envelope's data
    async episodes(podcastId, page) {
        const body = await client.get<Partial<EpisodesPage>>(`/podcasts/${podcastId}/episodes?page=${page}`);
        return { episodes: body.episodes ?? [], count: body.count || 0 };
    },

    async episodeBySlug(podcastId, slug) {
        const body = await client.get<Partial<EpisodeWithNeighbours> | null>(
            `/podcasts/${podcastId}/episodes/${encodeURIComponent(slug)}`
        );
        if (!body?.episode) {
            throw new Error(`Episode not found: ${slug}`);
        }
        return { episode: body.episode, next: body.next ?? null, previous: body.previous ?? null };
    },

    async seasons(podcastId) {
        return fromEither<number[]>(await client.get(`/podcasts/${podcastId}/seasons`), "seasons");
    },

    async season(podcastId, season) {
        const body = await client.get<Partial<Season>>(`/podcasts/${podcastId}/seasons/${season}`);
        return { season: body.season ?? season, episodes: body.episodes ?? [] };
    },

    async stats() {
        return fromEither<SiteStats>(await client.get("/podcasts/stats"), "stats");
    },

    async byIds(ids) {
        return fromEither<Podcast[]>(await client.get(`/podcasts/collection?ids=${ids.join(",")}`), "podcasts");
    },

    async similar(podcastId) {
        const podcasts = fromEither<Podcast[] | null>(await client.get(`/podcasts/${podcastId}/similar`), "podcasts");
        return (podcasts ?? []).filter((podcast) => podcast.id !== podcastId);
    },

    async similarEpisodes(episodeId) {
        const items = fromEither<Array<Episode | { episode: Episode }> | null>(
            await client.get(`/episodes/${episodeId}/similar`),
            "episodes"
        );
        // The Go API does not attach the podcast to these yet; without it there is nothing to link to
        return (items ?? []).map(episodeOf).filter((episode) => episode.id !== episodeId && episode.podcast);
    },

    async series(podcastId) {
        const series = fromEither<Array<Partial<Series>> | null>(
            await client.get(`/podcasts/${podcastId}/series`),
            "series"
        );
        return (series ?? []).flatMap((s) => {
            // Part order is the numbers of the parts: a series is not always published in order (37% of them were
            // published backwards), so the date is only what separates parts whose numbers cannot be read
            const episodes = (s.episodes ?? [])
                .map((e) => ({ ...e, part: e.episode_number ?? partNumber(e.title) }))
                .sort(
                    (a, b) =>
                        (a.part ?? Infinity) - (b.part ?? Infinity) ||
                        a.published.localeCompare(b.published) ||
                        a.id - b.id
                );
            if (s.id === undefined || !s.name || !s.slug || episodes.length === 0) {
                return [];
            }
            return [
                { id: s.id, name: s.name, slug: s.slug, episode_count: s.episode_count ?? episodes.length, episodes },
            ];
        });
    },
});
